import type { StaffRole } from "./enums";

/**
 * Permissions and the role map from docs/architecture/auth-tenancy.md.
 * Roles map to permissions in code. The server check is the one that counts;
 * clients use the same table only to hide what a user cannot do.
 */
export const PERMISSIONS = [
	"order.view",
	"order.create",
	"order.edit",
	"batch.decide",
	"order.cancelItem",
	"order.cancel",
	"kot.update",
	"bill.create",
	"payment.record",
	"bill.discount",
	"bill.void",
	"bill.reprint",
	"menu.availability",
	"menu.edit",
	"offers.manage",
	"tables.manage",
	"printers.manage",
	"reports.view",
	"audit.view",
	"day.close",
	"staff.manage",
	"settings.manage",
	"subscription.view",
	"restaurant.transferOwnership",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const kitchen: readonly Permission[] = ["order.view", "kot.update", "menu.availability"];

const waiter: readonly Permission[] = [...kitchen, "order.create", "order.edit", "batch.decide"];

/** Cashier discounts are limited by an owner setting, checked in billing. */
const cashier: readonly Permission[] = [
	...waiter,
	"order.cancelItem",
	"bill.create",
	"payment.record",
	"bill.discount",
	"bill.reprint",
	"printers.manage",
	"day.close",
];

/** Managers may manage staff except owners. That rule is checked in tenancy. */
const manager: readonly Permission[] = [
	...cashier,
	"order.cancel",
	"bill.void",
	"menu.edit",
	"offers.manage",
	"tables.manage",
	"reports.view",
	"audit.view",
	"staff.manage",
	"settings.manage",
];

const owner: readonly Permission[] = [
	...manager,
	"subscription.view",
	"restaurant.transferOwnership",
];

export const ROLE_PERMISSIONS: Readonly<Record<StaffRole, ReadonlySet<Permission>>> = {
	owner: new Set(owner),
	manager: new Set(manager),
	cashier: new Set(cashier),
	waiter: new Set(waiter),
	kitchen: new Set(kitchen),
};

export function can(role: StaffRole, permission: Permission): boolean {
	return ROLE_PERMISSIONS[role].has(permission);
}

export function permissionsFor(role: StaffRole): Permission[] {
	return PERMISSIONS.filter((p) => ROLE_PERMISSIONS[role].has(p));
}
